import { useGet } from 'common/hooks/useApi'
import { generateBasicSeoTags } from 'common/utils/seo'
import { GetServerSideProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { httpServer } from 'services/http'
import IndividualsNavbar from 'services/landing-page-individuals/components/individualsNavbar'
import { FullTestSuiteCard } from 'services/quizzes/components/FullTestSuiteCard'
import { IQuizWithCategories, QuizList } from 'services/quizzes/components/QuizList'
import { ICategories } from 'services/quizzes/quizzes.type'
import styled from 'styled-components'

const DemoQuizPage = ({
  quizzes: initialQuizzes,
  regions: initialRegions,
}: {
  quizzes: IQuizWithCategories[]
  regions: { country_id: string; country_name: string }[]
}) => {
  const router = useRouter()
  const [selectedRegion, setSelectedRegion] = useState<string>('global')
  const [selectedCategory, setSelectedCategory] = useState<{ [quizId: string]: ICategories | null }>({})
  const [search, setSearch] = useState('')

  // Client-side data fetching for quizzes when region changes
  const {
    data: quizzes = initialQuizzes,
    isLoading: isQuizzesLoading,
    error: quizzesError,
  } = useGet<IQuizWithCategories[]>(`/public/quizzes?countryId=${selectedRegion}`)

  // Client-side data fetching for regions
  const {
    data: regions = initialRegions,
    isLoading: isRegionsLoading,
    error: regionsError,
  } = useGet<{ country_id: string; country_name: string }[]>('/public/quizzes/regions')

  // Sync region with search params
  useEffect(() => {
    const regionParam = router.query.region as string
    if (regionParam && regionParam !== selectedRegion) {
      setSelectedRegion(regionParam)
    }
  }, [router.query.region, selectedRegion])

  const handleRegionChange = (region: string) => {
    setSelectedRegion(region)
    router.replace({ pathname: router.pathname, query: region === 'all' ? {} : { region } }, undefined, {
      shallow: true,
    })
  }

  const handleCategorySelect = (quiz: IQuizWithCategories, catId: string) => {
    const cat = quiz.categories.find((c) => c.id === catId) || null
    setSelectedCategory((prev) => ({ ...prev, [quiz.id]: cat }))
  }

  const startDemoTest = (quiz: IQuizWithCategories, categoryId?: string) => {
    const query: Record<string, string> = { quizId: quiz.id }
    if (categoryId) query.categoryId = categoryId
    const queryString = new URLSearchParams(query).toString()
    const url = `/demo-quiz-test?${queryString}`
    window.open(url, '_blank')
  }

  const isLoading = isRegionsLoading || isQuizzesLoading
  const error = regionsError || quizzesError
  const { t, i18n } = useTranslation('common')
  const baseUrl = 'https://seaplify.com'
  const currentLang = i18n.language || 'en'
  const canonicalUrl = `${baseUrl}/demo-quiz${currentLang !== 'en' ? `?lang=${currentLang}` : ''}`
  const supportedLangs = ['en', 'ro', 'bg', 'ph']
  const ogImage = 'https://seaplify.com/branding/og-image.png'
  const quizTitle = t('nav_tests', 'Advancement Tests')
  const quizDescription = t('landing-page-description', 'Take advancement tests for maritime certifications.')
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: quizTitle,
    description: quizDescription,
    itemListElement: quizzes.map((quiz, idx) => ({
      '@type': 'Quiz',
      position: idx + 1,
      name: quiz.name,
      url: `${baseUrl}/demo-quiz-test?quizId=${quiz.id}`,
      image: quiz.image || ogImage,
      dateModified: quiz.updatedAt,
    })),
  }
  return (
    <Div>
      <Head>
        {generateBasicSeoTags({
          title: quizTitle,
          description: quizDescription,
          url: canonicalUrl,
          image: ogImage,
          keywords: [
            'teste marinari',
            'ceronav',
            'fluviale',
            'conducător navă',
            'certificări maritime',
            'examene maritime',
            'teste de avansare',
            'teste de certificare',
            'teste de certificare maritime',
          ],
        })}
        <link rel="canonical" href={canonicalUrl} />
        <meta name="language" content={currentLang} />
        {supportedLangs.map((lang) => (
          <link
            key={lang}
            rel="alternate"
            hrefLang={lang}
            href={`${baseUrl}/demo-quiz${lang !== 'en' ? `?lang=${lang}` : ''}`}
          />
        ))}
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/demo-quiz`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Head>
      <IndividualsNavbar text="I'm an employer" />
      <FullTestSuiteCard />
      <QuizList
        quizzes={quizzes}
        regions={regions}
        selectedRegion={selectedRegion}
        selectedCategory={selectedCategory}
        error={error}
        loading={isLoading}
        search={search}
        onSearchChange={setSearch}
        onRegionChange={handleRegionChange}
        onCategorySelect={handleCategorySelect}
        onStartTest={startDemoTest}
      />
    </Div>
  )
}

DemoQuizPage.getLayout = (page: React.ReactNode | React.ReactNode[]) => page

export const getServerSideProps: GetServerSideProps = async ({ locale, query }) => {
  const countryId = typeof query.region === 'string' ? query.region : 'global'

  const quizzesUrl = `/public/quizzes?countryId=${countryId}`
  const [{ data: quizzes = [] }, { data: regions = [] }, translations] = await Promise.all([
    httpServer.get(quizzesUrl),
    httpServer.get('/public/quizzes/regions'),
    serverSideTranslations(locale || 'en', ['common']),
  ])
  return {
    props: {
      quizzes,
      regions,
      ...translations,
    },
  }
}

const Div = styled.div`
  padding: 1rem;
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
`

export default DemoQuizPage
